import { useEffect, useRef, useState } from 'react';
import Chat from '../graci/Chat.js';
import Context from '../graci/Context.js';
import Services from '../Services.js';
import Sockets from '../Sockets.js';
import StepFrame from './StepFrame.js';
import Theme from '../styles/Theme.js';
import Utils from '../Utils.js';

/*
 * ChatStep
 *
 * GRACI, inside the flow.
 *
 * This is where the flow's state becomes something the assistant can read.
 *
 * THE CONTEXT IS THE WHOLE POINT. Flow.payload already assembles the path, the
 * derived facts and the articles that were shown and rejected, because an
 * escalation needs exactly the same things a useful assistant does. It goes
 * over as instructions, so GRACI opens knowing the model, the age, the symptom
 * and what has already been ruled out.
 */

// The eleven values src/graci asks for, from our design system. See graci/tokens.js.
import { jsx as _jsx } from "react/jsx-runtime";
const tokens = () => ({
  accent: Theme.colors.primary(),
  background: Theme.colors.white,
  border: Theme.colors.border(),
  danger: Theme.colors.error,
  font: Theme.fonts.body,
  muted: Theme.colors.lightGrey,
  onAccent: Theme.colors.onPrimary(),
  radius: 16,
  subdued: Theme.colors.offWhite,
  subtext: Theme.colors.subText(),
  text: Theme.colors.text()
});

/*
 * How tall the conversation should be.
 *
 * MEASURED, NOT GUESSED. The alternative is subtracting a pile of constants for
 * the header, the page padding, the back button, the eyebrow, the title and the
 * help line, which is wrong the moment any of them changes and silently wrong
 * on the brands whose copy wraps to a different number of lines.
 *
 * IT DOES NOT RE-MEASURE ON SCROLL, AND THAT IS THE WHOLE POINT.
 * getBoundingClientRect().top is measured from the top of the viewport, so it
 * changes as the page scrolls. Feeding that back into the box's own height made
 * a loop: the box resized, the document got taller, the viewport scrolled, the
 * scroll handler measured a new top, the box resized again. On a desktop the
 * page barely scrolls and it settled; on a phone it did not, and it came out as
 * "Maximum call stack size exceeded" from inside a promise, which points
 * nowhere near the code that caused it.
 *
 * So it listens for resize only, and ignores changes under a couple of pixels.
 * The step scrolls itself to the top on entry, so measuring once there and
 * again when the viewport actually changes size is all that is needed.
 *
 * The visual viewport is still the one to watch: on iOS the software keyboard
 * does not change window.innerHeight, so a chat sized from that alone puts its
 * own input underneath the keyboard the moment somebody taps it.
 */
const MINIMUM = 340;

// Below this, a change is layout noise rather than a new viewport.
const THRESHOLD = 2;
const useAvailableHeight = gutter => {
  const box = useRef(null);
  const [height, setHeight] = useState(null);
  useEffect(() => {
    const measure = () => {
      let top = box.current?.getBoundingClientRect().top;
      if (typeof top !== 'number') {
        return;
      }
      let viewport = window.visualViewport?.height || window.innerHeight;
      let next = Math.max(MINIMUM, Math.round(viewport - top - gutter));
      setHeight(current => {
        return current !== null && Math.abs(current - next) < THRESHOLD ? current : next;
      });
    };
    measure();
    window.addEventListener('resize', measure);
    window.visualViewport?.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
      window.visualViewport?.removeEventListener('resize', measure);
    };
  }, [gutter]);
  return [box, height];
};
const ChatStep = ({
  brand,
  node,
  onContinue,
  payload,
  stepKey
}) => {
  const mobile = Utils.isMobile();

  // Breathing room under the box. Tighter on mobile, where the viewport is
  // the scarce thing and the footer is a long scroll away regardless.
  const [box, height] = useAvailableHeight(mobile ? 16 : 40);
  const context = Context.session({
    answers: payload?.path || [],
    brand: brand?.name,
    documents: payload?.documents_shown || [],
    /*
     * The dealership id, when this instance has one.
     *
     * The server narrows the assistant from it: white-label
     * naming, and hiding product lines the dealership never sold.
     * Without it a water-only customer can be given confident
     * troubleshooting for smoke sensors they do not own.
     */
    extra: {
      channel: 'support_center',
      dealership_id: Services.dealership()?.id || null
    },
    facts: payload?.facts || {},
    reason: payload?.reason
  });
  return /*#__PURE__*/_jsx(StepFrame, {
    eyebrow: 'GRACI',
    help: Context.hasAnswers(context) ? 'Our assistant already has everything you told us on the way here, so you will not be asked any of it again.' : 'Tell our assistant what is going on and it will help you work through it.',
    stepKey: stepKey,
    title: 'Let us keep going',
    children: /*#__PURE__*/_jsx("div", {
      ref: box,
      style: {
        border: `1px solid ${Theme.colors.border()}`,
        borderRadius: 16,
        display: 'flex',
        flexDirection: 'column',
        // null until the first measurement lands, one frame in.
        height: height || MINIMUM,
        padding: mobile ? '18px 16px 16px' : '22px 22px 18px'
      },
      children: /*#__PURE__*/_jsx(Chat, {
        config: {
          url: Sockets.url()
        },
        context: context,
        onClose: onContinue ? onContinue.bind(this, {
          next: node.next
        }) : null,
        tokens: tokens()
      })
    })
  });
};
export default ChatStep;